import { AxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  extractConflict,
  fetchLnpPage,
  updateLnp,
  bulkDeleteLnp,
  createImportJobFromData,
} from "../../api/lnp";
import useEditPreferenceStore from "../../store/useEditPreferenceStore";
import useEditStore from "../../store/useEditStore";
import useTableStore from "../../store/useTabStore";
import type { ColumnMeta, ILnp } from "../../type";
import LoadingSpinner from "../ui/LoadingSpinner";
import { SavePreviewModal, Backdrop } from "../ui/Modal";
import type { ConflictFieldRow, SavePreviewRowChange } from "../ui/Modal";
import EditableCell from "./EditableCell";
import Dropdown from "../ui/dropdown";
import BulkEditModal from "./BulkEditModal";
import { client } from "../../api/client";
import { bulkUpdateLnp } from "../../api/lnp";

interface Props {
  showConfirm: (config: {
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    cancelLabel?: string;
    variant?: "primary" | "danger" | "warning";
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
  showInfo: (config: {
    title: string;
    message: React.ReactNode;
    variant?: "success" | "info" | "warning";
  }) => void;
  showConflict: (config: {
    rowKey: string | number;
    rows: ConflictFieldRow[];
    onAcceptServer: () => void;
    onOverwrite: () => void;
  }) => void;
  closeConflict: () => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const EditableLnpTable: React.FC<Props> = ({
  showConfirm,
  showInfo,
  showConflict,
  closeConflict,
}) => {
  const { columns, fetchColumns } = useTableStore();
  const {
    dirtyRows,
    originalRows,
    setCell,
    discard,
    discardAll,
    applySaved,
    replaceOriginalFromServer,
    versions,
  } = useEditStore();

  const {
    visibleColumns: persistedVisibleColumns,
    pageSize,
    setPageSize,
    toggleColumn,
    resetPreferences,
  } = useEditPreferenceStore();

  const [rows, setRows] = useState<ILnp[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("lnp_id");
  const [selectedPks, setSelectedPks] = useState<Set<number>>(new Set());
  const [statusFilter, setStatusFilter] = useState("draft");
  const [saving, setSaving] = useState(false);

  const [columnPickerOpen, setColumnPickerOpen] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRowChanges, setPreviewRowChanges] = useState<
    SavePreviewRowChange[]
  >([]);

  const [metadataList, setMetadataList] = useState<
    { experiment_id: string; title: string }[]
  >([]);
  const [bulkEditMode, setBulkEditMode] = useState<"batch" | "selected" | null>(null);

  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const resp = await client.get("/search/admin/metadata/");
        if (resp.status === 200) {
          if (Array.isArray(resp.data)) {
            setMetadataList(resp.data);
          } else if (resp.data && resp.data.results) {
            setMetadataList(resp.data.results);
          }
        }
      } catch (err) {
        console.error("Failed to load metadata", err);
      }
    };
    loadMetadata();
  }, []);

  useEffect(() => {
    fetchColumns();
  }, [fetchColumns]);

  const loadPage = useCallback(() => {
    setLoading(true);
    fetchLnpPage({ 
      page, 
      pageSize, 
      search, 
      searchField: searchField || undefined,
      status: statusFilter === "all" ? undefined : statusFilter 
    })
      .then((data) => {
        setRows(data.results);
        setTotalCount(data.count);
      })
      .catch(() => {
        showInfo({
          title: "Failed to load",
          message: "Could not fetch LNP rows. Try again.",
          variant: "warning",
        });
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, search, searchField, statusFilter, showInfo]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const handleBulkDelete = () => {
    if (selectedPks.size === 0) return;
    showConfirm({
      title: "Delete Selected Rows",
      message: `Are you sure you want to delete ${selectedPks.size} selected row(s)? This action cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: async () => {
        try {
          await bulkDeleteLnp(Array.from(selectedPks));
          setRows((prev) => prev.filter((r) => !selectedPks.has(r.index)));
          setTotalCount((prev) => Math.max(0, prev - selectedPks.size));
          setSelectedPks(new Set());
          showInfo({
            title: "Deleted",
            message: `Selected rows were deleted successfully.`,
            variant: "success",
          });
        } catch (err: any) {
          const ax = err as AxiosError<{ detail?: string }>;
          showInfo({
            title: "Delete failed",
            message: ax.response?.data?.detail ?? "Could not delete rows.",
            variant: "warning",
          });
        }
      },
    });
  };

  const visibleColumns: ColumnMeta[] = useMemo(() => {
    if (!columns) return [];
    const set = new Set(persistedVisibleColumns);
    // Preserve the canonical column order returned by the backend.
    return columns.filter((c) => set.has(c.name));
  }, [columns, persistedVisibleColumns]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const dirtyCount = Object.keys(dirtyRows).length;
  const dirtyCellCount = Object.values(dirtyRows).reduce(
    (sum, r) => sum + Object.keys(r).length,
    0
  );

  const labelFor = (name: string): string => {
    const c = columns?.find((col) => col.name === name);
    return c ? c.label : name;
  };

  const runSaveAll = async () => {
    setSaving(true);
    try {
      await bulkUpdateLnp(dirtyRows);

      setSaving(false);
      discardAll(); // Clear dirty rows
      showInfo({
        title: "Saved Successfully",
        message: `Successfully updated ${dirtyCount} row(s).`,
        variant: "success",
      });
      // Refresh the page
      setTimeout(loadPage, 1000);

    } catch (err: any) {
      setSaving(false);
      const ax = err as AxiosError<{ detail?: string }>;
      showInfo({
        title: "Save Failed",
        message: ax.response?.data?.detail ?? "Could not update rows.",
        variant: "warning",
      });
    }
  };

  const handleSaveClick = () => {
    if (dirtyCount === 0) return;
    const rowChanges: SavePreviewRowChange[] = Object.entries(dirtyRows).map(
      ([pkStr, dirty]) => {
        const pk = Number(pkStr);
        const original = originalRows[pk];
        const changes = Object.entries(
          dirty as Record<string, unknown>
        ).map(([field, after]) => ({
          field,
          label: labelFor(field),
          before: original
            ? (original as unknown as Record<string, unknown>)[field]
            : undefined,
          after,
        }));
        return { pk, changes };
      }
    );
    // Stable order: row PK ascending, fields in column order if known.
    rowChanges.sort((a, b) => a.pk - b.pk);
    if (columns) {
      const order = new Map(columns.map((c, i) => [c.name, i]));
      for (const r of rowChanges) {
        r.changes.sort(
          (a, b) =>
            (order.get(a.field) ?? 999) - (order.get(b.field) ?? 999)
        );
      }
    }
    setPreviewRowChanges(rowChanges);
    setPreviewOpen(true);
  };

  const handleDiscardClick = () => {
    if (dirtyCount === 0) return;
    showConfirm({
      title: "Discard changes",
      message: `Discard all ${dirtyCellCount} pending change(s)? This cannot be undone.`,
      confirmLabel: "Discard",
      variant: "danger",
      onConfirm: discardAll,
    });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <div className="relative">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 mb-4 bg-white border border-gray-200 rounded-lg shadow-sm">
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 flex-wrap">
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            className="px-3 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Categories</option>
            <option value="il_name">IL Name</option>
            <option value="hl_name">HL Name</option>
            <option value="chl_name">CHL Name</option>
            <option value="peg_name">PEG Name</option>
            <option value="experiment_id__experiment_id">Experiment ID</option>
            <option value="import_job_id">Batch ID</option>
          </select>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search…"
            className="w-48 px-3 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            type="submit"
            className="px-3 py-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
          >
            Search
          </button>
        </form>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value);
            }}
            className="px-3 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">Status: All</option>
            <option value="draft">Status: Draft</option>
            <option value="published">Status: Published</option>
          </select>
          <button
            type="button"
            onClick={() => setBulkEditMode("batch")}
            className="px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
          >
            Bulk Edit by Batch
          </button>
          <button
            type="button"
            disabled={selectedPks.size === 0}
            onClick={() => setBulkEditMode("selected")}
            className="px-3 py-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:bg-blue-50"
          >
            Bulk Edit Selected ({selectedPks.size})
          </button>
          <button
            type="button"
            disabled={selectedPks.size === 0}
            onClick={handleBulkDelete}
            className="px-3 py-2 text-sm text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:bg-red-400"
          >
            Delete Selected ({selectedPks.size})
          </button>
          <ColumnPicker
            columns={columns ?? []}
            visible={persistedVisibleColumns}
            onToggle={toggleColumn}
            onReset={resetPreferences}
            isOpen={columnPickerOpen}
            setOpen={setColumnPickerOpen}
          />
          <select
            value={pageSize}
            onChange={(e) => {
              setPage(1);
              setPageSize(Number(e.target.value));
            }}
            className="px-2 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} / page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="relative bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center p-12">
            <LoadingSpinner />
          </div>
        ) : visibleColumns.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">
            No columns selected. Use the <span className="font-medium">Columns</span> picker.
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">
            No rows match your search.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-gray-700">
              <thead className="text-xs text-gray-600 uppercase bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selectedPks.size === rows.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPks(new Set(rows.map(r => r.index)));
                        } else {
                          setSelectedPks(new Set());
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  {visibleColumns.map((c) => (
                    <th
                      key={c.name}
                      className="px-3 py-2 font-medium border-r border-gray-200 whitespace-nowrap"
                      title={`${c.type}${c.editable ? "" : " · read-only"}`}
                    >
                      {c.label}
                      {!c.editable && (
                        <span className="ml-1 text-gray-400">🔒</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => {
                  const pk = row.index;
                  const renderKey = pk ?? `null-${(row as any).lnp_id ?? Math.random()}`;
                  const rowDirty = dirtyRows[pk];
                  const rowIsDirty = !!rowDirty;
                  return (
                    <tr
                      key={renderKey}
                      className={
                        rowIsDirty
                          ? "bg-amber-50/60"
                          : row.status === "draft"
                          ? "bg-emerald-50/40 hover:bg-emerald-50/70"
                          : "hover:bg-gray-50/60"
                      }
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedPks.has(pk)}
                          onChange={(e) => {
                            const newSet = new Set(selectedPks);
                            if (e.target.checked) newSet.add(pk);
                            else newSet.delete(pk);
                            setSelectedPks(newSet);
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      {visibleColumns.map((c) => {
                        const fieldName = c.name as keyof ILnp;
                        const baseValue = (row as unknown as Record<string, unknown>)[
                          c.name
                        ];
                        const dirtyValue = rowDirty
                          ? (rowDirty as Record<string, unknown>)[c.name]
                          : undefined;
                        const isCellDirty = rowDirty
                          ? c.name in rowDirty
                          : false;
                        return (
                          <EditableCell
                            key={c.name}
                            value={isCellDirty ? dirtyValue : baseValue}
                            column={c}
                            isDirty={isCellDirty}
                            row={row}
                            onCommit={(newValue) =>
                              setCell(
                                pk,
                                fieldName,
                                newValue as ILnp[typeof fieldName],
                                row
                              )
                            }
                          />
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {loading && rows.length > 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60">
            <LoadingSpinner />
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
        <div>
          {totalCount.toLocaleString()} rows · page {page} of {totalPages}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Prev
          </button>
          
          {(() => {
            const maxPages = 10;
            let start = Math.max(1, page - Math.floor(maxPages / 2));
            let end = start + maxPages - 1;
            if (end > totalPages) {
              end = totalPages;
              start = Math.max(1, end - maxPages + 1);
            }
            
            const pages = [];
            for (let i = start; i <= end; i++) {
              pages.push(
                <button
                  key={i}
                  type="button"
                  onClick={() => setPage(i)}
                  className={`px-3 py-1.5 rounded-lg border ${
                    i === page
                      ? "bg-blue-50 text-blue-600 border-blue-200 font-medium"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {i}
                </button>
              );
            }
            return pages;
          })()}

          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Sticky save bar */}
      {dirtyCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-amber-300 bg-amber-50/95 backdrop-blur supports-[backdrop-filter]:bg-amber-50/85 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex items-center justify-between max-w-screen-2xl px-6 py-3 mx-auto">
            <div className="text-sm text-amber-900">
              <span className="inline-block w-2 h-2 mr-2 align-middle rounded-full bg-amber-500" />
              <span className="font-semibold">
                {dirtyCellCount} change{dirtyCellCount === 1 ? "" : "s"}
              </span>{" "}
              across{" "}
              <span className="font-semibold">
                {dirtyCount} row{dirtyCount === 1 ? "" : "s"}
              </span>{" "}
              pending
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleDiscardClick}
                disabled={saving}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"
              >
                Discard all
              </button>
              <button
                type="button"
                onClick={handleSaveClick}
                disabled={saving}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
              >
                {saving
                  ? "Saving..."
                  : `Review & save ${dirtyCount} row${dirtyCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spacer so the sticky bar never covers the last row of the table */}
      {dirtyCount > 0 && <div className="h-20" aria-hidden />}

      <SavePreviewModal
        open={previewOpen}
        rowChanges={previewRowChanges}
        isSubmitting={saving}
        onConfirm={() => {
          setPreviewOpen(false);
          runSaveAll();
        }}
        onCancel={() => setPreviewOpen(false)}
      />

      {/* Bulk Edit Modal */}
      {bulkEditMode !== null && (
        <BulkEditModal
          mode={bulkEditMode}
          selectedPks={selectedPks}
          rows={rows}
          originalRows={originalRows}
          onClose={() => setBulkEditMode(null)}
          columns={columns ?? []}
          setCell={setCell}
          showInfo={showInfo}
          metadataList={metadataList}
        />
      )}
    </div>
  );
};

const ColumnPicker: React.FC<{
  columns: ColumnMeta[];
  visible: string[];
  onToggle: (name: string) => void;
  onReset: () => void;
  isOpen: boolean;
  setOpen: (v: boolean) => void;
}> = ({ columns, visible, onToggle, onReset, isOpen, setOpen }) => {
  const visibleSet = useMemo(() => new Set(visible), [visible]);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!isOpen)}
        className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        Columns ({visible.length})
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="absolute right-0 z-20 w-72 mt-2 overflow-y-auto bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 max-h-96">
            <div className="flex items-center justify-between px-4 py-2 text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
              <span>Saved on this device</span>
              <button
                type="button"
                onClick={onReset}
                className="text-blue-600 hover:underline"
              >
                Reset to default
              </button>
            </div>
            <div className="py-1">
              {columns.map((c) => (
                <label
                  key={c.name}
                  className="flex items-center px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
                >
                  <input
                    type="checkbox"
                    className="w-4 h-4 mr-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={visibleSet.has(c.name)}
                    onChange={() => onToggle(c.name)}
                  />
                  <span className="flex-1 truncate">{c.label}</span>
                  {!c.editable && (
                    <span className="ml-2 text-xs text-gray-400">read-only</span>
                  )}
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EditableLnpTable;
